import React, { FC, useContext, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import debounce from "lodash/debounce";

import { CATEGORY_LIST, CREATE_PRODUCT, PRODUCT_CREATE } from "../ProductConstants";
import ProductCategorySkeleton from "./ProductCategorySkeleton";
import CategoryTypeItem from "./CategoryTypeItem";
import CategoryItem from "./CategoryItem";
import { ProductsContext, TcategoryData } from "../ProductsContext";
import { AppContext } from "Context/AppContext";

import Typography from "Components/DesignSystem/Typography/Typography";
import Button from "Components/DesignSystem/Button/Button";
import Search from "Components/DesignSystem/Search/Search";
import Divider from "Components/DesignSystem/Divider/Divider";
import EmptyState from "Components/DesignSystem/EmptyState/EmptyState";
import NoDataIllustration from "Components/DesignSystem/EmptyState/NoDataIllustration";
import { toast } from "Components/DesignSystem/Toast/useToast";

/**
 * The categories browser at /gms/products — the first app screen on the design
 * system (§ 64): Typography/Search/Button/Divider/EmptyState/Skeleton in place
 * of the hand-rolled header, `.button-style`, react-toastify, and the legacy
 * pulse skeleton. Behavior (permission gates, cache fetch, 600ms search
 * debounce, sort/filter) is unchanged.
 */
const ProductCategoryList: FC = () => {
  const appData = useContext(AppContext);
  const productContext = useContext(ProductsContext);
  const createPermission = appData.pm.checkPermission("gmsproducts_create");
  const navigate = useNavigate();
  /*
    The DS Search is controlled, so the debounce that used to live inside
    CustomSearchBar moves here: `searchInput` echoes keystrokes into the field,
    `search` (what filtering reads) follows 600ms later — the same delay this
    page had before.
  */
  const [searchInput, setSearchInput] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const debouncedSetSearch = useMemo(() => debounce(setSearch, 600), []);
  useEffect(() => () => { debouncedSetSearch.cancel(); }, [debouncedSetSearch]);
  const industryId = appData?.get_selected_industry()?.id;

  const handleSearchChange = (value: string) => {
    const term = value.trimStart();
    setSearchInput(term);
    debouncedSetSearch(term);
  };

  useEffect(() => {
    if (!appData?.pm?.checkPermission("gmsproducts_read")) {
      navigate("/workspace/module/permission/denied");
      return;
    }
  }, []);

  // Category/category-type cache fetch used to auto-fire from ProductsContext itself on every
  // industry switch, app-wide (that provider is mounted globally). Moved here, the actual
  // consumer, keyed on industryId alone rather than gated on "cache already has data" -- that
  // gate would silently skip a legitimate refetch when switching to a different industry while
  // stale data from a previous one was still cached.
  useEffect(() => {
    if (industryId) {
      productContext.getCategoryTypeAndCategory();
    }
  }, [industryId]);

  const categoriesToDisplay = productContext.categoryData.filter(categoryType => {
    return categoryType?.category.filter(((cat: any) => cat.name.toLowerCase().includes(search?.toLowerCase()))).length > 0
  })

  const isCategoryPresent = categoriesToDisplay.some((categoryType) => categoryType.category && categoryType.category.length > 0)

  const sortedCategories = useMemo(() =>
    categoriesToDisplay
      ? [...categoriesToDisplay].sort((a: TcategoryData, b: TcategoryData) =>
        a.name.localeCompare(b.name))
      : []
    , [categoriesToDisplay])

  const searching = search.trim().length > 0;

  return (
    /* Ambient ink at the screen root (§ 58) — text colour comes from the theme, not tw-text-black. */
    /* § 268 — this root owns the page's horizontal inset, and it is ZERO (§ 267): the
       shell's `<main>` already carries `tw-px-4`, so a gutter retyped per band is a
       second one on top of it. Every band used to state its own and they disagreed —
       the header `tw-px-[21px]`, the divider / headings / EVERY TILE `tw-ml-5` (20px),
       four left edges 1px apart. The divider was the visible cost: `tw-ml-5` on a
       `tw-w-full` rule pushed its right edge 20px past this box (scrollWidth 1250 vs
       clientWidth 1230), so it rendered 41px beyond the Create Product button. */
    <div id="categories-page-inset" className="tw-text-ink tw-px-0">
      {/* § 269 — `tw-justify-between` distributed two children (title, right-group);
          with the search out on its own that is three, and justify-between would
          spread all three evenly. The explicit spacer states which gap is elastic —
          the same idiom §§ 264-265 and 267 use. */}
      <div id="categories-page-header" className="tw-pt-[12px] tw-flex tw-items-center">
        {/* § 268 — `heading-2`, not `heading-1`. Both are 18px; the difference is
            weight, 700 against 500, so this title did not read as bigger than its
            neighbours' — it read as bolder. `heading-2` + `as="h1"` is the app-wide
            pairing (19 pages), and `pageTitleConvention.test.ts` named this file as one
            of exactly two known outliers on a list documented "shrinks, never grows".
            That entry is deleted with this change. */}
        <Typography className="tw-text-ink" variant="display" as="h1" id="categories-page-title">
          {CATEGORY_LIST}
        </Typography>
        {/* § 269 — the search sits with the TITLE, not with the CTA. It filters the
            grid below, so it answers "what am I looking at"; beside Create Product it
            read as a second action. §§ 264, 265 and 267 put the Guide, Compatibility
            and Manage Party searches on the left for exactly this reason, and this
            page was left out on the grounds that its header "already looked fine" —
            true of the page alone, and wrong across four list pages that should read
            the same way. 288px (`tw-w-72`) is the width the other three search at; it
            was 256. DOM order is unchanged — the search already preceded the button —
            so tab order is what it was. */}
        <div id="categories-view-controls" className="tw-flex tw-items-center tw-ms-4">
          <Search
            value={searchInput}
            onChange={handleSearchChange}
            placeholder="Search category"
            aria-label="Search categories"
            data-testid="categories-search-input"
            className="tw-w-72 tw-min-w-0"
          />
        </div>
        <div className="tw-flex tw-flex-grow" />
        <Button
          id="categories-btn-create-product"
          data-testid="products-create-btn"
          className="tw-shrink-0"
          onClick={() => { if (createPermission) { navigate(PRODUCT_CREATE) } else { toast.info("You don't have permission to Create a Product") } }}
        >
          {CREATE_PRODUCT}
        </Button>
      </div>
      <Divider className="tw-mt-2" />

      {productContext.showLoader ? (
        <ProductCategorySkeleton />
      ) : isCategoryPresent ? (
        sortedCategories?.map((data: any, index: any) => {
          const categories = data?.category.filter(((cat: any) => cat.name.toLowerCase().includes(search?.toLowerCase())))

          if (categories.length <= 0) return <></>
          return (
            <React.Fragment key={data.name + index}>
              <CategoryTypeItem categoryTypeIndex={index} categoryTypename={data?.name} />
              {/* § 268 — the gutters are the CONTAINER's, not each tile's. Every tile
                  used to carry `tw-ml-5 tw-mt-3`, which is how the grid's inset came to
                  be restated once per category and why the first tile in a row set the
                  page's left edge. `gap-x-5`/`gap-y-3` reproduce the measured geometry
                  exactly — 216px tiles at 20px across, 12px down. */}
              <div id={`categories-type-${index}-container`} className={`tw-flex tw-flex-wrap tw-gap-x-5 tw-gap-y-3 tw-mt-3`}>
                {categories?.map((category: any, catIndex: number) => (
                  <React.Fragment key={category?.name}>
                    <CategoryItem categoryTypeIndex={index} categoryIndex={catIndex} categoryId={category?.id} categoryName={category?.name} />
                  </React.Fragment>
                ))}
              </div>
            </React.Fragment>
          )
        })
      ) : (
        <EmptyState
          id="categories-no-data"
          data-testid="products-empty-message"
          title={searching ? "No categories match your search" : "No categories found"}
          description={
            searching
              ? "Try a different search term."
              : "There are no categories in this environment yet."
          }
          illustration={<NoDataIllustration />}
        />
      )}
    </div>
  );
};

export default ProductCategoryList;
